/**
 * ProjectionContext — authored-tree walk helpers for callers that
 * need to reason about the row's *authored* parent chain regardless
 * of any projection-time relocation.
 *
 * For now `SurfaceProjection.project` is a pure function that returns
 * a `Result` rather than mutating the tree, so a row's `.parent`
 * always reflects the authored shape. These helpers therefore reduce
 * to ordinary parent walks. They live behind a `ProjectionContext`
 * facade so consumers (chiefly hide-write code on
 * `LonaDefaultRowOptions.allDayEvents`) can be written against an
 * abstract API and stay correct if a future change adds a renderer-
 * side tree where `.parent` no longer points at the authored parent.
 */

import type { Option } from "../core/option.ts";
import type { ModelRowReads } from "../rows/model-row-reads.ts";
import { kindOfLiveRow } from "../rows/row-kind.ts";
import { liveRowOf } from "../rows/model-row.ts";

/**
 * Walks a row's authored ancestors, ignoring the hoisting a projection
 * applies. Takes and answers rows as their reads (`ModelRowReads`): a row
 * read off `handle.model` or `row.variant` walks as is, and a live row is
 * its own reads.
 */
export namespace ProjectionContext {
  /** Authored parent of `row`, ignoring projection hoisting. */
  export function authoredParentOf(row: ModelRowReads): Option<ModelRowReads> {
    return row.parent ?? null;
  }

  /**
   * Walk up the authored tree from `row` (exclusive). Yields each
   * authored ancestor in order. Halts at the sheet root.
   */
  export function* authoredAncestors(
    row: ModelRowReads,
  ): IterableIterator<ModelRowReads> {
    let cursor = row.parent;
    while (cursor != null) {
      yield cursor;
      cursor = cursor.parent;
    }
  }

  /** First authored ancestor matching `pred`. */
  export function findAuthoredAncestor(
    row: ModelRowReads,
    pred: (r: ModelRowReads) => boolean,
  ): Option<ModelRowReads> {
    for (const ancestor of authoredAncestors(row)) {
      if (pred(ancestor)) return ancestor;
    }
    return null;
  }

  /**
   * Convenience: nearest alias ancestor whose descriptor template
   * equals `template`. Equivalent to:
   *
   *   findAuthoredAncestor(row, r =>
   *     r.descriptor.role === "alias" &&
   *     r.descriptor.template === template);
   */
  export function findOwningAlias(
    row: ModelRowReads,
    template: string,
  ): Option<ModelRowReads> {
    return findAuthoredAncestor(row, (r) => {
      const kind = kindOfLiveRow(liveRowOf(r));
      return kind.role === "alias" && kind.row.descriptor.template === template;
    });
  }
}
